/**
 * Which version of a bound shared list a schema column reads.
 *
 * One rule, both runtimes. It existed six times — the mint, the publish pin, the
 * builder grid, the link dialog and two more modals — and they disagreed, so the
 * grid offered an option the mint could not find and emitted the display string
 * into a permanent tracking code.
 *
 * A NULL pin means FOLLOW: the latest PUBLISHED version. Not the head. The head
 * can be a draft (`addVersion` writes one and moves `currentVersionId` to it)
 * and a draft is editable in place, so following the head lets a half-finished
 * vocabulary edit reach a code that is permanent once minted and joined against.
 *
 * A set pin means exactly that version, whatever its status — including a draft
 * or an archived one. Schemas published before this rule existed carry pins to
 * draft versions, and honouring them verbatim is what keeps their meaning
 * stable. "Healing" such a pin here would silently reassign what an already
 * published schema means.
 */
export function resolveBoundListVersion(list, ref) {
    const versions = list?.versions ?? [];
    if (versions.length === 0)
        return undefined;
    // Falsy, not `!= null`: a stored `''` means "no pin" here, and the SQL twin of
    // this rule has to spell that as NULLIF(pin, '') to agree. The two are only
    // equivalent by intent, so both say so.
    const pinnedId = ref?.listVersionId;
    if (pinnedId) {
        const version = versions.find((v) => v.id === pinnedId);
        // A pin naming a version that is gone is UNRESOLVABLE, never an invitation
        // to substitute a different one. The browser used to fall back to the newest
        // version, which swapped a frozen vocabulary for a live one in silence.
        return version ? { version, source: 'pinned', follows: false } : undefined;
    }
    // Max by `versionNumber` rather than `sort().at(0)` on a relation whose order
    // comes from whichever query loaded it. Every current query happens to order
    // DESC; a caller that forgets to must not change the answer.
    const published = versions
        .filter((v) => v.status === 'published')
        .reduce((best, v) => (!best || v.versionNumber > best.versionNumber ? v : best), undefined);
    if (published)
        return { version: published, source: 'published', follows: true };
    const head = versions.find((v) => v.id === list?.currentVersionId) ??
        versions.reduce((best, v) => (v.versionNumber > best.versionNumber ? v : best));
    return { version: head, source: 'unpublished', follows: true };
}
/**
 * Which two columns of a list a binding should read.
 *
 * By NAME, not by position. Callers used to take `columns[0]` and `columns[1]`,
 * which is right only while every list is exactly `[<Name>, Code]` — true of the
 * shipped catalog today and of nothing else. A list with a third column, or one
 * whose first column was removed and replaced (the editor appends, so every
 * later position shifts), then binds the wrong column and emits the wrong string
 * into a code that is permanent once minted. Nothing catches it: the value
 * column still exists, so no "column no longer exists" error fires, and the
 * wrong cell is a plausible string.
 *
 * Three passes, narrowest first. An exact `Code` is what the standard-lists
 * catalog guarantees for every list it seeds. A `code`-ish name is what a
 * hand-made list actually carries — `ISO code`, `Abbrev`, `Short code` — and
 * matching those is the whole point: an exact-only rule would fall back to
 * position on precisely the multi-column lists where position is dangerous, so
 * it would look stricter while helping least. Position is the last resort, for a
 * list that names neither column recognisably.
 *
 * The regex is `LinkListModal`'s, which had this right first and is the surface
 * where a person picks the columns by hand. Sharing it is the point of this
 * module: two disagreeing rules building bindings for one feature is the bug it
 * was created to remove.
 *
 * The value column is the first column that is NOT the code, so a leading column
 * added later does not change the answer.
 *
 * Returns undefined below two columns rather than a half-pair: a binding needs
 * both, and the callers' documented degradation is to keep the field's inline
 * values instead.
 */
const CODEISH = /code|abbrev|short/i;
/**
 * Tolerates a jsonb row with no `name`, which would otherwise throw on `.trim()`.
 *
 * The type says that cannot happen and the write DTOs enforce it, so the rule is
 * right about today's data — but columns live in jsonb written before those DTOs
 * existed, and the cost of being wrong here is a 500 on the seeding path rather
 * than a bad name. Cheap defence on a path that mints permanent codes.
 */
const columnName = (column) => 
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- jsonb predates the DTO that guarantees `name`
(column.name ?? '').trim();
export function resolveBoundColumnPair(columns) {
    if (!columns || columns.length < 2)
        return undefined;
    const code = columns.find((c) => columnName(c).toLowerCase() === 'code') ??
        columns.find((c) => CODEISH.test(columnName(c))) ??
        columns[1];
    const value = columns.find((c) => c !== code);
    return value ? { valueColumn: value, codeColumn: code } : undefined;
}
/**
 * The code column a new binding should start with, or `null` for "same as display value".
 *
 * `null` when the code column is blank on every row that has a display value. That is a
 * list of codes with no separate labels, and binding its empty Code column would flag every
 * row as missing a code, although the name comes out the same either way.
 */
export function defaultCodeColumnId(columns, rows) {
    const pair = resolveBoundColumnPair(columns);
    if (!pair)
        return undefined;
    const options = (rows ?? []).flatMap((r) => {
        const option = resolveListOption(r.cells[pair.valueColumn.id], r.cells[pair.codeColumn.id]);
        return option ? [option] : [];
    });
    if (options.length === 0)
        return pair.codeColumn.id;
    return options.some((option) => !option.codeWasBlank) ? pair.codeColumn.id : null;
}
/**
 * Reads one list row's value and code cells. Both are trimmed, an option with a blank value is
 * dropped (undefined), and a blank code falls back to the value. Pass `undefined` for the code cell
 * when the binding reads no code column. The bound-list resolvers in the app and the server, the
 * list sheet's warnings and `defaultCodeColumnId` read options through this.
 */
export function resolveListOption(valueCell, codeCell) {
    const value = (valueCell ?? '').trim();
    if (!value)
        return undefined;
    const code = (codeCell ?? '').trim();
    return { value, code: code || value, codeWasBlank: !code };
}
